<template>
  <section
    class="rounded-12 bg-elev-context-menu-bg py-2 shadow-overlay text-fg-base-fg3"
    @click.stop="$emit('hideMenu')"
  >
    <button
      v-if="isGuestOnline"
      :disabled="!registered"
      :title="$t('common.callUserNameParticipant', { name: guest.name })"
      class="button flex h-8 w-full items-center px-2 py-1 text-left text-1316 text-fg-base-fg3 hover:bg-surf-neut-soft-hov"
      @click="call(roomPServed, guest.pServed)"
    >
      <wui-icon
        class="icon mr-2 text-fg-base-fg22"
        :name="registered ? 'phoneM' : 'callDisabled'"
      />
      <span>{{ $t('common.makeCall') }}</span>
    </button>
    <button
      data-test="remove-member"
      class="button flex h-8 w-full items-center px-2 py-1 text-left text-1316 text-fg-base-fg3 hover:bg-surf-neut-soft-hov"
      :title="$t('common.removeUserNameFromChat', {name: guest.name })"
      @click="removeFromRoom"
    >
      <wui-icon
        class="icon-danger mr-2 text-fg-base-fg22"
        :name="isRemoving ? 'spinner' : 'trashM'"
      />
      <span>{{ $t('chat.removeFromGroup') }}</span>
    </button>
  </section>
</template>

<script setup lang="ts">
import { useRenderNotifier, WuiIcon } from '@wui/common-library'
import { computed, PropType, ref } from 'vue'


import { useAutoMarkAsReadIfExecutor } from '@/entities/chat-handlers'
import { Guest } from '@/entities/conference-participant'
import { chatModel } from '@/entities/messages'
import { useStoreRooms } from '@/entities/room'
import { useWebRTC } from '@/entities/webrtc'

import { useLocalization } from '@/shared/composables/localization'
import { useStoreGuestStatus } from '@/shared/guest-status'

import { call, remove } from '../'

const props = defineProps({
  guest: {
    type: Object as PropType<Guest>,
    required: true,
  },
  roomId: {
    type: String,
    required: true,
  },
  roomPServed: {
    type: String,
    required: true,
  },
})

defineEmits<{
  (event: 'hideMenu'): void
}>()

const { registered } = useWebRTC()
const { getStatusByPServed } = useStoreGuestStatus()
const isGuestOnline = computed(() => getStatusByPServed(props.guest.pServed)?.isOnline === true)
const storeRooms = useStoreRooms()
const { notifyError, notifySuccess } = useRenderNotifier()
const { t } = useLocalization()
const autoMarkAsReadIfExecutor = useAutoMarkAsReadIfExecutor()

const isRemoving = ref<boolean>(false)
const removeFromRoom = () => {
  isRemoving.value = true
  remove(props.roomPServed, props.guest.pServed)
    .then((data) => {
      if (data) {
        storeRooms.removeGuest(props.roomId, props.guest.pServed)
        chatModel.useChatMessageStore().addMessage(props.roomPServed, data)
        autoMarkAsReadIfExecutor(data, props.roomPServed)
        notifySuccess(t('guest.guestNameRemovedFromGroup', { name: props.guest.name }))
      } else {
        notifyError(t('guest.failedToRemoveGuestFromRoom', { name: props.guest.name }))
      }
    }).catch(() => {
      notifyError(t('guest.failedToRemoveGuestFromRoom', { name: props.guest.name }))
    }).finally(() => {
      isRemoving.value = false
    })
}

</script>

<style scoped>
@reference "../../../app/assets/tailwind.css";

  .button:hover .icon {
    @apply text-surf-brand-base-def;
  }

  .button:hover .icon-danger {
    @apply text-warning-shades-800;
  }
</style>
